import { useEffect, useRef, useState } from 'react';

/**
 * useDbWake — asks crm-api to make sure the CRM database is running (POST /api/system/wake) and polls
 * until it is. The database is stopped every night by the cost scheduler and started on demand, so the
 * first person to open the CRM after that waits ~2–5 minutes; this hook is what starts the clock.
 *
 * States:
 *   checking — first call in flight
 *   ready    — the database answers; nothing to show
 *   starting — Azure is starting it (or crm-api itself is still booting): keep polling
 *   unknown  — crm-api cannot start it here (not configured / Azure refused): show the message, stop polling
 *   timeout  — still not ready after MAX_WAIT_MS: show the message, stop polling
 */
export type DbWakeUiState = 'checking' | 'ready' | 'starting' | 'unknown' | 'timeout';

export interface DbWake {
  state: DbWakeUiState;
  message: string;
  /** Seconds since this page started waiting. */
  elapsedS: number;
  /** True once the state has been something other than ready/checking — i.e. this page actually waited. */
  waited: boolean;
}

const POLL_MS = 8_000;
const MAX_WAIT_MS = 10 * 60_000;

interface WakeResponse {
  state: 'ready' | 'starting' | 'unknown';
  message: string;
}

export function useDbWake(): DbWake {
  const [state, setState] = useState<DbWakeUiState>('checking');
  const [message, setMessage] = useState('Checking the CRM database…');
  const [elapsedS, setElapsedS] = useState(0);
  const [waited, setWaited] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async (): Promise<void> => {
      let next: WakeResponse;
      try {
        const res = await fetch('/api/system/wake', { method: 'POST' });
        if (!res.ok) throw new Error(String(res.status));
        next = (await res.json()) as WakeResponse;
      } catch {
        // crm-api scaling up from zero (or restarting) answers 502/503/504 or not at all for a while.
        next = { state: 'starting', message: 'Starting the CRM service…' };
      }
      if (cancelled) return;
      const elapsed = Date.now() - startedAt.current;
      setElapsedS(Math.round(elapsed / 1000));
      if (next.state === 'ready') {
        setState('ready');
        setMessage(next.message);
        return;
      }
      setWaited(true);
      if (next.state === 'unknown') {
        setState('unknown');
        setMessage(next.message);
        return;
      }
      if (elapsed > MAX_WAIT_MS) {
        setState('timeout');
        setMessage('The CRM database is taking longer than usual to start. Refresh in a few minutes, or ask an administrator.');
        return;
      }
      setState('starting');
      setMessage(next.message);
      timer = setTimeout(() => void poll(), POLL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Tick the elapsed counter between polls so the wait visibly moves.
  useEffect(() => {
    if (state !== 'starting') return;
    const t = setInterval(() => setElapsedS(Math.round((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [state]);

  return { state, message, elapsedS, waited };
}
